import React, { useCallback, useEffect, useState } from "react";
import Shell from "@/components/Shell";
import EmptyState from "@/components/EmptyState";
import AiReplyCard from "@/components/outreach/AiReplyCard";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Reply, Eye, MousePointerClick, ArrowUpRight, ArrowDownLeft, Send, RefreshCw, MessageSquare, UserCheck, CalendarDays, Inbox as InboxIcon, Phone, Gift, Mic, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { StatusPill, fmtTime } from "@/components/outreach/shared";

const FILTERS = [["all", "All"], ["replies", "Replies"], ["sent", "Sent"], ["failed", "Failed"], ["chats", "Chats"]];

export default function Inbox() {
  const [filter, setFilter] = useState("all");
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);

  const loadThreads = useCallback(async () => {
    const chatReq = api.get("/widget/sessions");
    if (filter === "chats") {
      const { data } = await chatReq;
      setChats(data);
      setActiveChat((id) => (id && data.some((c) => c.id === id) ? id : data[0]?.id || null));
      return;
    }
    const [{ data }, { data: chatData }] = await Promise.all([api.get("/threads", { params: { filter } }), chatReq]);
    setChats(chatData);
    // Website chats appear in "All" as well, so nothing is hidden behind the Chats tab.
    const merged = filter === "all"
      ? [...data, ...chatData.map((c) => ({ thread_id: `chat:${c.id}`, is_chat: true, chat: c, contact_name: c.visitor?.name || c.visitor?.email || "Website visitor", last: { created_at: c.updated_at || c.created_at, channel: "chat", subject: `Website chat${c.lead_magnet_unlocked_at ? " · guide downloaded" : c.status === "captured" ? " · lead captured" : ""}` }, count: c.messages?.length || 0, inbound: 0, opened: 0, failed: 0, unread: 0 }))].sort((a, b) => String(b.last?.created_at || "").localeCompare(String(a.last?.created_at || "")))
      : data;
    setThreads(merged);
    setActiveId((id) => (id && merged.some((t) => t.thread_id === id) ? id : merged[0]?.thread_id || null));
  }, [filter]);
  const loadMessages = useCallback(async () => {
    if (!activeId || activeId.startsWith("chat:")) { setMessages([]); return; }
    const { data } = await api.get("/messages", { params: { thread_id: activeId } });
    setMessages(data);
  }, [activeId]);

  useEffect(() => { loadThreads(); api.get("/email-accounts").then((r) => setAccounts(r.data)); }, [loadThreads]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  const active = threads.find((t) => t.thread_id === activeId);
  const refresh = () => { loadThreads(); loadMessages(); };

  return (
    <Shell title="Unified Inbox" actions={<Button variant="ghost" size="icon" className="w-8 h-8" onClick={refresh} data-testid="inbox-refresh"><RefreshCw className="w-4 h-4" /></Button>}>
      <div className="grid grid-cols-1 md:grid-cols-[22rem_1fr] h-[calc(100vh-3.5rem)] min-h-0" data-testid="inbox-grid">
        <div className="border-r border-border flex flex-col min-h-0">
          <div className="flex border-b border-border" data-testid="inbox-filters">
            {FILTERS.map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`inbox-filter-${k}`}
                className={`flex-1 py-2 text-xs font-medium transition-colors duration-150 border-b-2 ${filter === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40"}`}>{label}{k === "chats" && chats.length > 0 && <span className="ml-1 mono text-[10px] text-muted-foreground" data-testid="inbox-chats-count">{chats.length}</span>}</button>
            ))}
          </div>
          <div className="overflow-y-auto flex-1">
            {filter === "chats" && chats.map((c) => (
              <button key={c.id} onClick={() => setActiveChat(c.id)} data-testid={`chat-${c.id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-border transition-colors duration-150 ${activeChat === c.id ? "bg-muted" : "hover:bg-muted/50"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium truncate flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />{c.visitor?.name || c.visitor?.email || "Anonymous visitor"}</div>
                  <div className="text-[10px] mono text-muted-foreground shrink-0">{fmtTime(c.updated_at)}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate">{c.messages?.[c.messages.length - 1]?.content}</div>
                <div className="mt-1 flex items-center gap-2 text-[10px] mono text-muted-foreground flex-wrap">
                  <span>{c.messages?.length || 0} msgs</span>
                  {c.visitor?.phone && <span className="inline-flex items-center gap-0.5" data-testid={`chat-phone-${c.id}`}><Phone className="w-3 h-3" />{c.visitor.phone}</span>}
                  {c.status === "captured" && <span className="inline-flex items-center gap-0.5 text-emerald-500"><UserCheck className="w-3 h-3" />lead</span>}
                  {c.lead_magnet_unlocked_at && <span className="inline-flex items-center gap-0.5 text-amber-500" data-testid={`chat-downloaded-${c.id}`}><Gift className="w-3 h-3" />guide</span>}
                  {c.language && c.language !== "en" && <span className="uppercase border border-border rounded px-1" data-testid={`chat-lang-${c.id}`}>{c.language}</span>}
                  {c.messages?.some((m) => m.via === "voice") && <Mic className="w-3 h-3" data-testid={`chat-voice-${c.id}`} />}
                </div>
              </button>
            ))}
            {filter === "chats" && chats.length === 0 && <div className="p-4"><EmptyState testId="chats-empty" icon={MessageSquare} title="No website chats yet" hint="Install the widget from Settings and conversations will land here." /></div>}
            {filter !== "chats" && threads.map((t) => (
              <button key={t.thread_id} onClick={() => setActiveId(t.thread_id)} data-testid={`thread-${t.thread_id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-border transition-colors duration-150 ${activeId === t.thread_id ? "bg-muted" : "hover:bg-muted/50"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className={`text-sm truncate ${t.unread ? "font-semibold" : "font-medium"}`}>{t.contact_name || t.counterpart}</div>
                  <div className="text-[10px] mono text-muted-foreground shrink-0">{fmtTime(t.last.created_at)}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate">{t.last.channel && t.last.channel !== "email" && <span className="mono text-[9px] uppercase tracking-[0.08em] text-primary border border-primary/30 bg-primary/10 px-1 rounded mr-1.5" data-testid={`thread-channel-${t.thread_id}`}>{t.last.channel}</span>}{t.last.subject}</div>
                <div className="mt-1 flex items-center gap-2 text-[10px] mono text-muted-foreground">
                  <span>{t.count} msg{t.count === 1 ? "" : "s"}</span>
                  {t.inbound > 0 && <span className="inline-flex items-center gap-0.5 text-blue-500"><Reply className="w-3 h-3" />{t.inbound}</span>}
                  {t.opened > 0 && <span className="inline-flex items-center gap-0.5 text-emerald-500"><Eye className="w-3 h-3" />{t.opened}</span>}
                  {t.failed > 0 && <span className="inline-flex items-center gap-0.5 text-rose-500"><AlertTriangle className="w-3 h-3" />{t.failed}</span>}
                  {t.unread > 0 && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />}
                </div>
              </button>
            ))}
            {filter !== "chats" && threads.length === 0 && <div className="p-4"><EmptyState testId="inbox-empty" icon={InboxIcon} title={filter !== "all" ? "Nothing in this filter" : "No conversations yet"} hint={filter !== "all" ? "Try another filter." : "Enroll contacts in a sequence and replies will show up here."} /></div>}
          </div>
        </div>
        <div className="flex flex-col min-h-0">
          {filter === "chats" ? <ChatTranscript chat={chats.find((c) => c.id === activeChat)} /> : active?.is_chat ? <ChatTranscript chat={active.chat} /> : active ? (
            <>
              <div className="px-6 py-3 border-b border-border flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-base font-semibold tracking-tight truncate" data-testid="thread-title">{active.contact_name || active.counterpart}</div>
                  <div className="text-xs mono text-muted-foreground truncate">{active.counterpart}{active.company_name ? ` · ${active.company_name}` : ""}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="thread-messages">
                {messages.map((m) => <MessageCard key={m.id} m={m} />)}
              </div>
              <AiReplyCard thread={active} accountId={accounts[0]?.id} onSent={refresh} />
              {active.last?.channel && active.last.channel !== "email" ? <TextComposer thread={active} onSent={refresh} /> : <ReplyComposer thread={active} messages={messages} accounts={accounts} onSent={refresh} />}
            </>
          ) : <div className="p-10 text-sm text-muted-foreground">Pick a conversation to view.</div>}
        </div>
      </div>
    </Shell>
  );
}

function ChatTranscript({ chat }) {
  if (!chat) return <div className="p-10 text-sm text-muted-foreground">Pick a chat to view.</div>;
  const v = chat.visitor || {};
  const spokeVoice = (chat.messages || []).some((m) => m.via === "voice");
  return (
    <>
      <div className="px-6 py-3 border-b border-border">
        <div className="text-base font-semibold tracking-tight flex items-center gap-2" data-testid="chat-title">{v.name || v.email || "Anonymous visitor"}{chat.language && chat.language !== "en" && <span className="text-[10px] mono uppercase border border-border rounded px-1.5 py-px text-muted-foreground" data-testid="chat-language">{chat.language}</span>}</div>
        <div className="text-xs mono text-muted-foreground truncate">{[v.email, v.phone, v.company, chat.page_url].filter(Boolean).join(" · ") || "No details captured yet"}</div>
        <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]" data-testid="chat-badges">
          {chat.contact_id && <Link to={`/contacts?open=${chat.contact_id}`} className="inline-flex items-center gap-1 border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 rounded-full px-2 py-0.5 hover:bg-emerald-500/20 transition-colors" data-testid="chat-open-contact"><UserCheck className="w-3 h-3" />Contact + deal in CRM</Link>}
          {v.phone && <span className="inline-flex items-center gap-1 border border-border rounded-full px-2 py-0.5" data-testid="chat-phone"><Phone className="w-3 h-3" />{v.phone}</span>}
          {chat.lead_magnet_unlocked_at ? <span className="inline-flex items-center gap-1 border border-amber-500/30 bg-amber-500/10 text-amber-600 rounded-full px-2 py-0.5" data-testid="chat-downloaded"><Gift className="w-3 h-3" />Downloaded {chat.lead_magnet_title || "the guide"}{chat.lead_magnet_downloads ? ` ×${chat.lead_magnet_downloads}` : ""}</span>
            : chat.wants_resource ? <span className="inline-flex items-center gap-1 border border-border rounded-full px-2 py-0.5 text-muted-foreground"><Gift className="w-3 h-3" />Asked for the guide — details incomplete</span> : null}
          {chat.booking_id && <span className="inline-flex items-center gap-1 border border-primary/30 bg-primary/10 text-primary rounded-full px-2 py-0.5" data-testid="chat-booked"><Calendar className="w-3 h-3" />Meeting booked</span>}
          {spokeVoice && <span className="inline-flex items-center gap-1 border border-border rounded-full px-2 py-0.5 text-muted-foreground" data-testid="chat-voice"><Mic className="w-3 h-3" />Used voice</span>}
          {chat.intent && <span className="text-muted-foreground">Intent: {chat.intent}</span>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-2" data-testid="chat-messages">
        {(chat.messages || []).map((m, i) => (
          <div key={i} className={`max-w-xl rounded-md px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary/10 border border-primary/30 ml-auto" : "bg-card border border-border"}`} data-testid={`chat-msg-${m.role}`}>
            <div className="text-[10px] mono text-muted-foreground mb-0.5 flex items-center gap-1">{m.role === "user" ? "Visitor" : "Concierge"}{m.via === "voice" && <Mic className="w-2.5 h-2.5" />} · {new Date(m.at).toLocaleTimeString()}</div>
            {m.content}
          </div>
        ))}
      </div>
    </>
  );
}

function MessageCard({ m }) {
  const inbound = m.direction === "inbound";
  return (
    <div className={`border rounded-md p-4 max-w-3xl ${inbound ? "border-blue-500/30 bg-blue-500/5" : "border-border bg-card"}`} data-testid={`message-${m.id}`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {inbound ? <ArrowDownLeft className="w-3.5 h-3.5 text-blue-500" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
        <span className="mono">{inbound ? `from ${m.from_email}` : `to ${m.to_email}`}</span>
        <span>·</span>
        <span className="mono">{new Date(m.created_at).toLocaleString()}</span>
        {m.step_index != null && <span className="mono">· step {m.step_index + 1}</span>}
        <span className="ml-auto flex items-center gap-2">
          {m.opened_at && <span className="inline-flex items-center gap-0.5 text-emerald-500"><Eye className="w-3 h-3" />{m.open_count}</span>}
          {m.click_count > 0 && <span className="inline-flex items-center gap-0.5 text-emerald-500"><MousePointerClick className="w-3 h-3" />{m.click_count}</span>}
          <StatusPill status={m.status} />
        </span>
      </div>
      <div className="mt-2 text-sm font-medium">{m.subject}</div>
      {m.error && <div className="mt-2 border border-destructive/40 bg-destructive/10 text-destructive text-xs p-2 rounded-md mono">{m.error}</div>}
      <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{m.body}</div>
    </div>
  );
}

function TextComposer({ thread, onSent }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const channel = thread.last.channel;
  const send = async () => {
    setSending(true);
    try { await api.post("/messages/text", { contact_id: thread.contact_id, body, channel }); toast.success(`${channel.toUpperCase()} sent`); setBody(""); onSent(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSending(false); }
  };
  return (
    <div className="border-t border-border p-4 space-y-2" data-testid="text-composer">
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={1000} placeholder={`Reply by ${channel.toUpperCase()}…`} className="text-sm" data-testid="text-body" />
      <div className="flex items-center justify-between text-xs text-muted-foreground"><span className="mono">{body.length}/1000</span><Button size="sm" disabled={sending || !body.trim() || !thread.contact_id} onClick={send} data-testid="text-send"><Send className="w-3.5 h-3.5 mr-1.5" />{sending ? "Sending..." : `Send ${channel.toUpperCase()}`}</Button></div>
    </div>
  );
}

function ReplyComposer({ thread, messages, accounts, onSent }) {
  const [body, setBody] = useState("");
  const [accountId, setAccountId] = useState("");
  const [sending, setSending] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  useEffect(() => { setBody(""); }, [thread?.thread_id]);
  useEffect(() => { api.get("/calendar/settings").then((r) => setBookingUrl(r.data.booking_url)).catch(() => {}); }, []);
  useEffect(() => { if (!accountId && accounts[0]) setAccountId(accounts[0].id); }, [accounts, accountId]);
  const last = messages[messages.length - 1];
  const lastInbound = [...messages].reverse().find((m) => m.direction === "inbound");

  const send = async () => {
    if (!accountId) { toast.error("Add an email account in Settings first."); return; }
    if (!body.trim()) return;
    setSending(true);
    try {
      const subject = (last?.subject || "").startsWith("Re:") ? last.subject : `Re: ${last?.subject || ""}`;
      const { data } = await api.post("/messages/reply", { account_id: accountId, to_email: thread.counterpart, subject, body, thread_id: thread.thread_id, contact_id: thread.contact_id, in_reply_to: (lastInbound || last)?.message_id || null });
      if (data.result.ok) { toast.success("Reply sent"); setBody(""); } else toast.error(`Send failed: ${data.result.error}`);
      onSent();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  return (
    <div className="border-t border-border p-4 space-y-2" data-testid="reply-composer">
      <Textarea data-testid="reply-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder={`Reply to ${thread.counterpart}...`} className="text-sm" />
      <div className="flex items-center gap-2">
        <Select value={accountId} onValueChange={setAccountId}>
          <SelectTrigger className="h-8 w-64 text-xs" data-testid="reply-account"><SelectValue placeholder="Send from..." /></SelectTrigger>
          <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.from_email}</SelectItem>)}</SelectContent>
        </Select>
        {bookingUrl && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setBody((b) => `${b}${b && !b.endsWith("\n") ? "\n\n" : ""}Grab a time that works for you: ${bookingUrl}`)} data-testid="reply-insert-booking"><CalendarDays className="w-3.5 h-3.5 mr-1.5" />Insert booking link</Button>}
        <Button size="sm" className="ml-auto" onClick={send} disabled={sending || !body.trim()} data-testid="reply-send"><Send className="w-3.5 h-3.5 mr-1.5" />{sending ? "Sending..." : "Send reply"}</Button>
      </div>
    </div>
  );
}
